import { useCallback, useEffect, useRef, useState } from 'react'

import usePreviousProps from './previous_props_hook'

export const useFormMemory = (formId, initialInputValues) => {
  const [inputValues, setInputValues] = useState({})

  const prevFormId = usePreviousProps(formId)
  const unmounted = useRef(false)

  const isPrevFormEmpty = useCallback(() => {
    if (!prevFormId) return

    const keys = Object.keys(initialInputValues)

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      if (inputValues[prevFormId] && inputValues[prevFormId][key] !== initialInputValues[key]) {
        return false
      }
    }
    return true
  }, [initialInputValues, inputValues, prevFormId])

  const deleteFormEntry = useCallback(
    (formId) => {
      const id = formId || prevFormId
      setInputValues((prev) => {
        const { [id]: omit, ...result } = prev
        return result
      })
    },
    [prevFormId],
  )

  const initialiseForm = useCallback(() => {
    if (formId && !unmounted.current) {
      setInputValues((prev) => ({ ...prev, [formId]: initialInputValues }))
    }
  }, [formId, initialInputValues])

  useEffect(() => {
    if (prevFormId !== formId) {
      if (formId && !inputValues[formId]) {
        initialiseForm()
      }

      if (isPrevFormEmpty()) {
        deleteFormEntry()
      }
    }
  }, [
    deleteFormEntry,
    formId,
    initialiseForm,
    inputValues,
    isPrevFormEmpty,
    prevFormId,
  ])

  useEffect(() => {
    return () => {
      unmounted.current = true
    }
  }, [])

  const setInputValue = useCallback(
    (name, value) => {
      setInputValues((prev) => ({
        ...prev,
        [formId]: {
          ...prev[formId],
          [name]: value,
        },
      }))
    },
    [formId],
  )

  const handleChange = useCallback(
    (event) => {
      const name = event.currentTarget.name
      const value = event.currentTarget.value

      setInputValue(name, value)
    },
    [setInputValue],
  )

  return {
    formInputValues: inputValues[formId]
      ? inputValues[formId]
      : initialInputValues,
    handleChange,
    initialiseForm,
    deleteFormEntry,
    setInputValue,
  }
}
